import React, { useState, useEffect } from 'react';
import { HitApi } from '../../Api/ApiHit';
import {
  addHomeSection,
  updateHomeSection,
  searchGroup,
  searchCategory,
  searchSubCategory,
  searchService
} from '../../constant/Constant';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Save, Loader2, Plus, Trash2,
  LayoutGrid, Columns, ListIcon, GripVertical, Package, ChevronDown, ChevronUp
} from 'lucide-react';

const DISPLAY_TYPES = [
  { value: 'grid', label: 'Grid', icon: <LayoutGrid className="w-4 h-4" /> },
  { value: 'carousel', label: 'Carousel', icon: <Columns className="w-4 h-4" /> },
  { value: 'list', label: 'List', icon: <ListIcon className="w-4 h-4" /> }
];

const AddHomeSection = ({ appCategoryId, unitId, editData, onClose }) => {
  const isEditing = !!editData;

  // Form fields
  const [title, setTitle] = useState(editData?.title || '');
  const [subtitle, setSubtitle] = useState(editData?.subtitle || '');
  const [description, setDescription] = useState(editData?.description || '');
  const [displayType, setDisplayType] = useState(editData?.displayType || 'grid');
  const [columns, setColumns] = useState(editData?.columns || 3);
  const [sectionType, setSectionType] = useState(editData?.sectionType || 'services');
  const [order, setOrder] = useState(editData?.order || 0);
  const [isActive, setIsActive] = useState(editData?.isActive !== false);
  const [saving, setSaving] = useState(false);

  // Items arrays
  const [sectionServices, setSectionServices] = useState(
    (editData?.services || []).filter(s => s.serviceId)
  );
  const [sectionCategories, setSectionCategories] = useState(
    (editData?.categories || []).filter(c => c.categoryId || c.subCategoryId)
  );
  const [sectionPackages, setSectionPackages] = useState(editData?.packages || []);
  const [expandedPackage, setExpandedPackage] = useState(null);

  // Cascading dropdown state
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [services, setServices] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');

  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await HitApi({ page: 1, limit: 100000 }, searchGroup);
      setGroups(res?.data?.docs || res?.data || []);
    } catch {
      toast.error('Failed to fetch groups');
    }
    setLoadingGroups(false);
  };

  const handleGroupChange = async (groupId) => {
    setSelectedGroup(groupId);
    setSelectedCategory('');
    setSelectedSubCategory('');
    setCategories([]);
    setSubCategories([]);
    setServices([]);
    if (!groupId) return;

    setLoadingCategories(true);
    try {
      const res = await HitApi({ page: 1, limit: 100000, groupId }, searchCategory);
      setCategories(res?.data?.docs || res?.data || []);
    } catch {
      toast.error('Failed to fetch categories');
    }
    setLoadingCategories(false);
  };

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubCategory('');
    setSubCategories([]);
    setServices([]);
    if (!categoryId) return;

    setLoadingSubCategories(true);
    try {
      const res = await HitApi({ page: 1, limit: 100000, categoryId }, searchSubCategory);
      setSubCategories(res?.data?.docs || res?.data || []);
    } catch {
      toast.error('Failed to fetch subcategories');
    }
    setLoadingSubCategories(false);
  };

  const handleSubCategoryChange = async (subCategoryId) => {
    setSelectedSubCategory(subCategoryId);
    setServices([]);
    if (!subCategoryId) return;

    setLoadingServices(true);
    try {
      const res = await HitApi({
        page: 1,
        limit: 100000,
        search: { subCategoryId, groupId: selectedGroup }
      }, searchService);
      setServices(res?.data?.docs || res?.data || []);
    } catch {
      toast.error('Failed to fetch services');
    }
    setLoadingServices(false);
  };

  // Service helpers
  const isServiceAdded = (id) => sectionServices.some(s => (s.serviceId?._id || s.serviceId) === id);

  const addService = (service) => {
    if (isServiceAdded(service._id)) return;
    setSectionServices(prev => [...prev, { serviceId: service, order: prev.length }]);
  };

  const removeService = (id) => {
    const actualId = id?._id || id;
    setSectionServices(prev => prev.filter(s => (s.serviceId?._id || s.serviceId) !== actualId));
  };

  // Category helpers
  const isCategoryAdded = (id, itemType) => {
    return sectionCategories.some(c => {
      if (itemType === 'category') return (c.categoryId?._id || c.categoryId) === id && c.itemType === 'category';
      return (c.subCategoryId?._id || c.subCategoryId) === id && c.itemType === 'subcategory';
    });
  };

  const addCategory = (item, itemType) => {
    if (isCategoryAdded(item._id, itemType)) return;
    const data = { itemType, order: sectionCategories.length };
    if (itemType === 'category') {
      data.categoryId = item;
    } else {
      data.subCategoryId = item;
      if (selectedCategory) {
        const cat = categories.find(c => c._id === selectedCategory);
        if (cat) data.categoryId = cat;
      }
    }
    setSectionCategories(prev => [...prev, data]);
  };

  const removeCategory = (index) => {
    setSectionCategories(prev => prev.filter((_, i) => i !== index));
  };

  // Package helpers
  const addPackage = () => {
    setSectionPackages(prev => [...prev, {
      name: '',
      description: '',
      price: 0,
      discountPrice: 0,
      services: [],
      isActive: true,
      order: prev.length
    }]);
    setExpandedPackage(sectionPackages.length);
  };

  const updatePackage = (index, field, value) => {
    setSectionPackages(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const removePackage = (index) => {
    setSectionPackages(prev => prev.filter((_, i) => i !== index));
    setExpandedPackage(null);
  };

  const addServiceToPackage = (pkgIndex, service) => {
    setSectionPackages(prev => prev.map((p, i) => {
      if (i !== pkgIndex) return p;
      const exists = p.services.some(s => (s.serviceId?._id || s.serviceId) === service._id);
      if (exists) return p;
      return { ...p, services: [...p.services, { serviceId: service, order: p.services.length }] };
    }));
  };

  const removeServiceFromPackage = (pkgIndex, serviceId) => {
    const actualId = serviceId?._id || serviceId;
    setSectionPackages(prev => prev.map((p, i) => {
      if (i !== pkgIndex) return p;
      return { ...p, services: p.services.filter(s => (s.serviceId?._id || s.serviceId) !== actualId) };
    }));
  };

  const isServiceInPackage = (pkgIndex, serviceId) => {
    const pkg = sectionPackages[pkgIndex];
    if (!pkg) return false;
    return pkg.services.some(s => (s.serviceId?._id || s.serviceId) === serviceId);
  };

  // Save
  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    const payload = {
      appCategoryId,
      unitId,
      title: title.trim(),
      subtitle: subtitle.trim(),
      description: description.trim(),
      displayType,
      columns: parseInt(columns) || 3,
      sectionType,
      order: parseInt(order) || 0,
      isActive,
      services: sectionType === 'services'
        ? sectionServices.map((s, i) => ({ serviceId: s.serviceId?._id || s.serviceId, order: i }))
        : [],
      categories: sectionType === 'categories'
        ? sectionCategories.map((c, i) => ({
            categoryId: c.categoryId?._id || c.categoryId || undefined,
            subCategoryId: c.subCategoryId?._id || c.subCategoryId || undefined,
            itemType: c.itemType,
            order: i
          }))
        : [],
      packages: sectionType === 'packages'
        ? sectionPackages.map((p, i) => ({
            name: p.name,
            description: p.description || '',
            image: p.image || '',
            price: parseFloat(p.price) || 0,
            discountPrice: parseFloat(p.discountPrice) || 0,
            services: p.services.map((s, j) => ({ serviceId: s.serviceId?._id || s.serviceId, order: j })),
            isActive: p.isActive !== false,
            order: i
          }))
        : []
    };

    if (isEditing) payload._id = editData._id;

    try {
      console.log('HomeSection payload:', JSON.stringify(payload));
      const res = await HitApi(payload, isEditing ? updateHomeSection : addHomeSection);
      console.log('HomeSection response:', JSON.stringify(res));
      if (res?.success || res?.statusCode === 200 || res?.statusCode === 201) {
        toast.success(isEditing ? 'Section updated' : 'Section created');
        onClose(true);
      } else {
        toast.error(res?.message || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save section');
    }
    setSaving(false);
  };

  const getItemName = (item) => item?.name || item?.serviceName || item?._id || 'Unknown';

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => onClose(false)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isEditing ? 'Edit Section' : 'Add Section'}</h1>
          <p className="text-sm text-gray-500">Configure the home section details</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Basic Info</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Section title"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <input
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="Section subtitle"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Section description"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <input
                type="number"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <button
                onClick={() => setIsActive(!isActive)}
                className={`w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  isActive
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-red-50 text-red-700 border-red-200'
                }`}
              >
                {isActive ? 'Active' : 'Inactive'}
              </button>
            </div>
          </div>
        </div>

        {/* Display & Section Type */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Display Settings</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Type</label>
            <div className="flex gap-2">
              {DISPLAY_TYPES.map((dt) => (
                <button
                  key={dt.value}
                  onClick={() => setDisplayType(dt.value)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    displayType === dt.value
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {dt.icon} {dt.label}
                </button>
              ))}
            </div>
          </div>

          {displayType === 'grid' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Columns</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <button
                    key={n}
                    onClick={() => setColumns(n)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium border transition-colors ${
                      columns === n
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section Type</label>
            <div className="flex gap-2">
              {['services', 'categories', 'packages'].map((type) => (
                <button
                  key={type}
                  onClick={() => setSectionType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors capitalize ${
                    sectionType === type
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Picker Section */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {sectionType === 'services' ? 'Add Services' : sectionType === 'packages' ? 'Add Packages' : 'Add Categories'}
          </h2>

          {/* Cascading Dropdowns */}
          {sectionType !== 'packages' && <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => handleGroupChange(e.target.value)}
                disabled={loadingGroups}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              >
                <option value="">Select Group</option>
                {groups.map((g) => (
                  <option key={g._id} value={g._id}>{g.name || g._id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                disabled={!selectedGroup || loadingCategories}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-50"
              >
                <option value="">{loadingCategories ? 'Loading...' : 'Select Category'}</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>{c.name || c._id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sub Category</label>
              <select
                value={selectedSubCategory}
                onChange={(e) => handleSubCategoryChange(e.target.value)}
                disabled={!selectedCategory || loadingSubCategories}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:bg-gray-50"
              >
                <option value="">{loadingSubCategories ? 'Loading...' : 'Select Sub Category'}</option>
                {subCategories.map((sc) => (
                  <option key={sc._id} value={sc._id}>{sc.name || sc._id}</option>
                ))}
              </select>
            </div>
          </div>}

          {/* Add Category/SubCategory buttons (when sectionType is categories) */}
          {sectionType === 'categories' && (
            <div className="flex gap-2">
              {selectedCategory && (
                <button
                  onClick={() => {
                    const cat = categories.find(c => c._id === selectedCategory);
                    if (cat) addCategory(cat, 'category');
                  }}
                  disabled={!selectedCategory || isCategoryAdded(selectedCategory, 'category')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-3 h-3" /> Add Category
                </button>
              )}
              {selectedSubCategory && (
                <button
                  onClick={() => {
                    const sc = subCategories.find(s => s._id === selectedSubCategory);
                    if (sc) addCategory(sc, 'subcategory');
                  }}
                  disabled={!selectedSubCategory || isCategoryAdded(selectedSubCategory, 'subcategory')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-3 h-3" /> Add Sub Category
                </button>
              )}
            </div>
          )}

          {/* Available Services List */}
          {sectionType === 'services' && services.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Available Services ({services.length})</p>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {services.map((service) => {
                  const added = isServiceAdded(service._id);
                  return (
                    <div key={service._id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{service.name || service.serviceName}</p>
                        {service.price && <p className="text-xs text-gray-500">₹{service.price}</p>}
                      </div>
                      <button
                        onClick={() => added ? removeService(service._id) : addService(service)}
                        className={`p-1.5 rounded text-xs font-medium ${
                          added
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {added ? <Trash2 className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Available Categories/SubCategories List (for sectionType categories) */}
          {sectionType === 'categories' && categories.length > 0 && !selectedSubCategory && selectedGroup && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Available Categories ({categories.length})</p>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {categories.map((cat) => {
                  const added = isCategoryAdded(cat._id, 'category');
                  return (
                    <div key={cat._id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                      <p className="text-sm font-medium text-gray-900">{cat.name}</p>
                      <button
                        onClick={() => added ? null : addCategory(cat, 'category')}
                        disabled={added}
                        className={`p-1.5 rounded text-xs font-medium ${
                          added ? 'text-gray-300 cursor-not-allowed' : 'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {added ? 'Added' : <Plus className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Packages Builder */}
          {sectionType === 'packages' && (
            <div className="space-y-3">
              <button
                onClick={addPackage}
                className="flex items-center gap-2 px-3 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800"
              >
                <Plus className="w-3.5 h-3.5" /> New Package
              </button>

              {sectionPackages.map((pkg, pkgIndex) => (
                <div key={pkgIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Package Header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedPackage(expandedPackage === pkgIndex ? null : pkgIndex)}
                  >
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-900">{pkg.name || `Package ${pkgIndex + 1}`}</span>
                      <span className="text-xs text-gray-500">{pkg.services.length} services</span>
                      {pkg.price > 0 && <span className="text-xs text-gray-500">₹{pkg.price}</span>}
                      {!pkg.isActive && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Inactive</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); removePackage(pkgIndex); }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {expandedPackage === pkgIndex ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>

                  {/* Package Body */}
                  {expandedPackage === pkgIndex && (
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Package Name *</label>
                          <input
                            value={pkg.name}
                            onChange={(e) => updatePackage(pkgIndex, 'name', e.target.value)}
                            placeholder="e.g. Bridal Package"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                          <input
                            value={pkg.description}
                            onChange={(e) => updatePackage(pkgIndex, 'description', e.target.value)}
                            placeholder="Package description"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Price (₹)</label>
                          <input
                            type="number"
                            value={pkg.price}
                            onChange={(e) => updatePackage(pkgIndex, 'price', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Discount Price (₹)</label>
                          <input
                            type="number"
                            value={pkg.discountPrice}
                            onChange={(e) => updatePackage(pkgIndex, 'discountPrice', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                          <button
                            onClick={() => updatePackage(pkgIndex, 'isActive', !pkg.isActive)}
                            className={`w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                              pkg.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
                            }`}
                          >
                            {pkg.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </div>
                      </div>

                      {/* Service picker for this package */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Add Services to Package</p>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <select
                            value={selectedGroup}
                            onChange={(e) => handleGroupChange(e.target.value)}
                            className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-black"
                          >
                            <option value="">Group</option>
                            {groups.map((g) => (
                              <option key={g._id} value={g._id}>{g.name || g._id}</option>
                            ))}
                          </select>
                          <select
                            value={selectedCategory}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            disabled={!selectedGroup}
                            className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
                          >
                            <option value="">{loadingCategories ? 'Loading...' : 'Category'}</option>
                            {categories.map((c) => (
                              <option key={c._id} value={c._id}>{c.name || c._id}</option>
                            ))}
                          </select>
                          <select
                            value={selectedSubCategory}
                            onChange={(e) => handleSubCategoryChange(e.target.value)}
                            disabled={!selectedCategory}
                            className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-50"
                          >
                            <option value="">{loadingSubCategories ? 'Loading...' : 'Sub Category'}</option>
                            {subCategories.map((sc) => (
                              <option key={sc._id} value={sc._id}>{sc.name || sc._id}</option>
                            ))}
                          </select>
                        </div>

                        {loadingServices && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 py-1">
                            <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                          </div>
                        )}

                        {services.length > 0 && (
                          <div className="max-h-36 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-100">
                            {services.map((service) => {
                              const inPkg = isServiceInPackage(pkgIndex, service._id);
                              return (
                                <div key={service._id} className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50">
                                  <div>
                                    <p className="text-xs font-medium text-gray-900">{service.name || service.serviceName}</p>
                                    {service.price && <p className="text-[10px] text-gray-500">₹{service.price}</p>}
                                  </div>
                                  <button
                                    onClick={() => inPkg ? removeServiceFromPackage(pkgIndex, service._id) : addServiceToPackage(pkgIndex, service)}
                                    className={`p-1 rounded ${inPkg ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                                  >
                                    {inPkg ? <Trash2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Package services list */}
                      {pkg.services.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">Services in Package ({pkg.services.length})</p>
                          <div className="space-y-1">
                            {pkg.services.map((s, sIdx) => {
                              const svc = s.serviceId || {};
                              return (
                                <div key={sIdx} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-400">{sIdx + 1}</span>
                                    <p className="text-xs font-medium text-gray-900">{getItemName(svc)}</p>
                                    {svc.price && <span className="text-[10px] text-gray-500">₹{svc.price}</span>}
                                  </div>
                                  <button onClick={() => removeServiceFromPackage(pkgIndex, svc._id || s.serviceId)} className="p-0.5 text-red-500 hover:bg-red-50 rounded">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {sectionPackages.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No packages created yet. Click "New Package" to start.</p>
              )}
            </div>
          )}

          {loadingServices && sectionType !== 'packages' && (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading services...
            </div>
          )}
        </div>

        {/* Selected Items */}
        <div className="bg-white rounded-lg shadow border border-gray-100 p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Selected {sectionType === 'services'
              ? `Services (${sectionServices.length})`
              : sectionType === 'packages'
              ? `Packages (${sectionPackages.length})`
              : `Categories (${sectionCategories.length})`
            }
          </h2>

          {sectionType === 'services' && sectionServices.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">No services added yet</p>
          )}

          {sectionType === 'services' && sectionServices.map((item, index) => {
            const service = item.serviceId || {};
            return (
              <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                  <span className="text-xs text-gray-400 w-5">{index + 1}</span>
                  <p className="text-sm font-medium text-gray-900">{getItemName(service)}</p>
                  {service.price && <span className="text-xs text-gray-500">₹{service.price}</span>}
                </div>
                <button onClick={() => removeService(service._id || item.serviceId)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}

          {sectionType === 'packages' && sectionPackages.length > 0 && sectionPackages.map((pkg, index) => (
            <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                <span className="text-xs text-gray-400 w-5">{index + 1}</span>
                <Package className="w-3.5 h-3.5 text-gray-500" />
                <p className="text-sm font-medium text-gray-900">{pkg.name || 'Untitled'}</p>
                <span className="text-xs text-gray-500">{pkg.services.length} services</span>
                {pkg.price > 0 && <span className="text-xs text-gray-500">₹{pkg.discountPrice > 0 ? pkg.discountPrice : pkg.price}</span>}
                {pkg.discountPrice > 0 && pkg.price > 0 && <span className="text-xs text-gray-400 line-through">₹{pkg.price}</span>}
              </div>
              <button onClick={() => removePackage(index)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          {sectionType === 'packages' && sectionPackages.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">No packages created yet</p>
          )}

          {sectionType === 'categories' && sectionCategories.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">No categories added yet</p>
          )}

          {sectionType === 'categories' && sectionCategories.map((item, index) => {
            const cat = item.categoryId || {};
            const subCat = item.subCategoryId || {};
            return (
              <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                  <span className="text-xs text-gray-400 w-5">{index + 1}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${item.itemType === 'category' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {item.itemType}
                  </span>
                  <p className="text-sm font-medium text-gray-900">
                    {item.itemType === 'category' ? getItemName(cat) : getItemName(subCat)}
                  </p>
                </div>
                <button onClick={() => removeCategory(index)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3 pt-2 pb-6">
          <button
            onClick={() => onClose(false)}
            className="px-5 py-2.5 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : isEditing ? 'Update Section' : 'Create Section'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddHomeSection;
